import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SortableTree, SimpleTreeItemWrapper } from "dnd-kit-sortable-tree";
import { assignElementToGroup, updateNodeById, fromTreeItems, removeNodeById } from "../../utils/treeUtils";
import { Element, Task as TaskType, Collection, CollectionGroup, Note, Habit, Timer, Stopwatch, TimeTracker } from "../../types";
import { useTranslation } from "react-i18next";

import "./TaskList.css";
import { Task } from "./task/Task";
import { EditTask } from "./edit-task/EditTask";
import { NoteElement } from "../elements/NoteElement";
import { HabitElement } from "../elements/HabitElement";
import { TimerElement } from "../elements/TimerElement";
import { StopwatchElement } from "../elements/StopwatchElement";
import { TimeTrackerElement } from "../elements/TimeTrackerElement";
import { AddElement } from "../add-task/AddTask";
import { AddTimerModal } from "../add-timer-modal/AddTimerModal";
import { AddStopwatchModal } from "../add-stopwatch-modal/AddStopwatchModal";
import { AddTimeTrackerModal } from "../add-time-tracker-modal/AddTimeTrackerModal";
import { NoteModal } from "../note-modal/NoteModal";
import { AddHabitModal } from "../add-habit-modal/AddHabitModal";
import { createNextRecurringTask, getLastOverdueRecurringDueDate } from '../../utils/taskRecurrence';
import { getTaskDateUpdates } from '../daily-planner/taskReschedule';
import { SectionModal } from "../section-modal/SectionModal";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import ActionMenu from "../action-menu/ActionMenu";
import {
    faChevronDown,
    faChevronRight,
    faPlus,
    faCaretDown,
    faPencil,
    faTrash,
    faCheck,
    faNoteSticky,
    faRepeat,
    faClock,
    faStopwatch,
    faRoute,
    faLayerGroup,
    faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type AddOptionType = 'task' | 'note' | 'habit' | 'timer' | 'stopwatch' | 'timeTracker';

interface TaskListProps {
    todos: Element[];
    setTodos: (todos: Element[]) => void;
    deleteTodo: (id: string) => void;
    editTodo: (id: string) => void;
    updateTodo: (updatedTodo: Element) => void;
    saveTodo: (content: string, id: string) => void;
    lists: Collection[];
    selectedListId: string;
    moveTaskToList: (task: TaskType, listId: string) => void;
    moveSubtaskToList: (subtask: TaskType, listId: string) => void;
    scrollRef: React.RefObject<HTMLDivElement>;
    showReorderHandles: boolean;
    addElement: (element: Element, groupId?: string) => void;
    groups?: CollectionGroup[];
    toggleGroup?: (groupId: string) => void;
    renameGroup?: (groupId: string, name: string) => void;
    deleteGroup?: (groupId: string) => void;
    moveElementToList: (element: Element, listId: string) => void;
    /** Legacy display setting; retained only for stored-data/test compatibility. */
    visibleTypes?: string[];
}

type TreeElement = Element & { children?: TreeElement[]; collapsed?: boolean };
type TreeElementRenderer = (item: TreeElement, depth: number) => React.ReactNode;
type SubtaskEditorSlot = { portalId: string; parentDepth: number };
type TreeRenderContextValue = {
    renderElement: TreeElementRenderer;
    subtaskEditorSlotsAfterItem: Map<string, SubtaskEditorSlot[]>;
};

const TREE_INDENTATION_WIDTH = 20;
const getSubtaskEditorPortalId = (listId: string, taskId: string) =>
    `subtask-editor-${listId}-${taskId}`;

const getSubtaskEditorSlotsAfterItem = (
    items: TreeElement[],
    listId: string,
    depth = 0,
    slots = new Map<string, SubtaskEditorSlot[]>(),
): Map<string, SubtaskEditorSlot[]> => {
    const visitItem = (item: TreeElement, itemDepth: number): string => {
        let lastVisibleItemId = item.id;

        if (item.children?.length) {
            const childLastVisibleIds = item.children.map((child) => visitItem(child, itemDepth + 1));
            if (!item.collapsed) {
                lastVisibleItemId = childLastVisibleIds[childLastVisibleIds.length - 1];
            }
        }

        if (item.type === 'task' || !item.type) {
            const anchorSlots = slots.get(lastVisibleItemId) || [];
            anchorSlots.push({
                portalId: getSubtaskEditorPortalId(listId, item.id),
                parentDepth: itemDepth,
            });
            slots.set(lastVisibleItemId, anchorSlots);
        }

        return lastVisibleItemId;
    };

    items.forEach((item) => visitItem(item, depth));

    return slots;
};

const TreeRenderContext = React.createContext<TreeRenderContextValue | null>(null);

/* eslint-disable @typescript-eslint/no-explicit-any */
const TreeItemComponent = React.forwardRef<HTMLDivElement, any>((props: any, ref) => {
    const treeRenderContext = React.useContext(TreeRenderContext);
    const subtaskEditorSlots = treeRenderContext?.subtaskEditorSlotsAfterItem.get(props.item.id) || [];

    return (
        <>
        <SimpleTreeItemWrapper
            {...props}
            ref={ref}
            contentClassName={`collection-tree-row collection-tree-row-${props.item.type || 'task'}`}
            disableCollapseOnItemClick={true}
            manualDrag={true}
        >
            {(props.item.children?.length ?? 0) > 0 && !props.item.edit && (
                <button
                    type="button"
                    onClick={props.onCollapse}
                    className="task-collapse-slot"
                    aria-label={props.collapsed ? 'Expand task' : 'Collapse task'}
                >
                    <FontAwesomeIcon
                        className="collapsed-icon"
                        icon={props.collapsed ? faChevronRight : faChevronDown}
                    />
                </button>
            )}

            {treeRenderContext?.renderElement(props.item as TreeElement, props.depth || 0)}
        </SimpleTreeItemWrapper>
        {subtaskEditorSlots.map((slot) => (
            <div
                id={slot.portalId}
                key={slot.portalId}
                data-testid={`subtask-editor-slot-${slot.portalId}`}
                className="subtask-editor-slot"
                style={{ paddingLeft: slot.parentDepth * TREE_INDENTATION_WIDTH }}
            />
        ))}
        </>
    );
});
/* eslint-enable @typescript-eslint/no-explicit-any */

export const TaskList: React.FC<TaskListProps> = ({
    todos,
    setTodos,
    deleteTodo,
    editTodo,
    updateTodo,
    saveTodo,
    lists,
    selectedListId,
    moveTaskToList,
    moveSubtaskToList,
    scrollRef,
    showReorderHandles,
    addElement,
    groups = [{ id: 'general', name: 'General' }],
    toggleGroup,
    renameGroup,
    deleteGroup,
    moveElementToList,
}) => {
    const { t } = useTranslation();
    const [showAddTimerModal, setShowAddTimerModal] = useState(false);
    const [showAddStopwatchModal, setShowAddStopwatchModal] = useState(false);
    const [showAddTimeTrackerModal, setShowAddTimeTrackerModal] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [editingNote, setEditingNote] = useState<Note | undefined>(undefined);
    const [showAddHabitModal, setShowAddHabitModal] = useState(false);
    const [activeHabitHistoryId, setActiveHabitHistoryId] = useState<string | undefined>(undefined);
    const [activeHabitHistoryMonth, setActiveHabitHistoryMonth] = useState(new Date());
    const [addType, setAddType] = useState<AddOptionType>(() => {
        const savedType = window.localStorage.getItem('minderBox:lastAddType')
            || window.localStorage.getItem('easyDone:lastAddType');
        return ['task', 'note', 'habit', 'timer', 'stopwatch', 'timeTracker'].includes(savedType || '')
            ? savedType as AddOptionType
            : 'task';
    });
    const [addMenuGroupId, setAddMenuGroupId] = useState<string | null>(null);
    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
    const [addMenuPosition, setAddMenuPosition] = useState({ top: 0, left: 0 });
    const [activeAddGroupId, setActiveAddGroupId] = useState<string>('general');
    const [expandedTaskGroupId, setExpandedTaskGroupId] = useState<string | null>(null);
    const [groupToRename, setGroupToRename] = useState<CollectionGroup | null>(null);
    const [groupToDelete, setGroupToDelete] = useState<CollectionGroup | null>(null);
    const [groupToClearCompleted, setGroupToClearCompleted] = useState<CollectionGroup | null>(null);
    const [elementToMoveSection, setElementToMoveSection] = useState<Element | null>(null);

    useEffect(() => {
        if (todos.length > 0) return;

        setAddType('task');
    }, [selectedListId, todos.length]);

    useEffect(() => {
        if (!addMenuAnchor) return;

        const updatePosition = () => {
            const rect = addMenuAnchor.getBoundingClientRect();
            const menuWidth = 210;
            const menuHeight = 260;
            const top = window.innerHeight - rect.bottom >= menuHeight
                ? rect.bottom + 6
                : Math.max(8, rect.top - menuHeight - 6);
            const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
            setAddMenuPosition({ top, left });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [addMenuAnchor]);

    const closeAddMenu = () => {
        setAddMenuGroupId(null);
        setAddMenuAnchor(null);
    };

    const openHabitHistory = (id: string) => {
        setActiveHabitHistoryId(id);
        setActiveHabitHistoryMonth(new Date());
    };

    const closeHabitHistory = () => {
        setActiveHabitHistoryId(undefined);
    };

    const moveElementToSection = (element: Element, groupId: string) => {
        const currentGroupId = element.groupId || 'general';
        if (currentGroupId === groupId) {
            setElementToMoveSection(null);
            return;
        }

        setTodos([
            ...removeNodeById(todos, element.id),
            assignElementToGroup(element, groupId),
        ]);
        setElementToMoveSection(null);
    };
    
    const toggleComplete = (id: string) => {
        let nextRecurringTask: TaskType | undefined;
        const updatedTodos = updateNodeById(todos, id, (node) => {
            // Only toggle completion for task elements
            if (node.type === "task" || !node.type) {
                const task = node as TaskType;
                const completed = !task.completed;
                if (completed && task.recurrence) nextRecurringTask = createNextRecurringTask(task);
                return {
                    ...task,
                    completed,
                    completedAt: completed ? new Date().toISOString() : undefined,
                    recurrence: completed ? undefined : task.recurrence,
                    recurrencePending: undefined,
                };
            }
            return node;
        });
        setTodos(nextRecurringTask ? [...updatedTodos, nextRecurringTask] : updatedTodos);
    };

    const skipToLatestOverdue = (task: TaskType) => {
        const dueDate = getLastOverdueRecurringDueDate(task);
        if (!dueDate) return;
        setTodos(updateNodeById(todos, task.id, (node) => ({ ...node, ...getTaskDateUpdates(node as TaskType, new Date(dueDate)) }) as Element));
    };

    const isTaskElement = (element: Element): element is TaskType =>
        element.type === 'task' || !element.type;

    const hasCompletedTasks = (elements: Element[]): boolean => elements.some((element) => {
        if (!isTaskElement(element)) return false;
        return !!element.completed || hasCompletedTasks((element.subtasks || []) as Element[]);
    });

    const removeCompletedTasks = (elements: Element[]): Element[] => elements.reduce((remaining, element) => {
        if (!isTaskElement(element)) {
            remaining.push(element);
            return remaining;
        }

        if (element.completed) return remaining;

        remaining.push({
            ...element,
            subtasks: removeCompletedTasks((element.subtasks || []) as Element[]) as TaskType[],
        });
        return remaining;
    }, [] as Element[]);

    const clearCompletedTasksInGroup = (groupId: string) => {
        setTodos(todos.reduce((updated, element) => {
            if ((element.groupId || 'general') !== groupId) {
                updated.push(element);
                return updated;
            }

            updated.push(...removeCompletedTasks([element]));
            return updated;
        }, [] as Element[]));
    };

    const renderElement = (item: TreeElement, depth: number) => {
        const isTask = item.type === 'task' || !item.type;
        const onMoveToSection = groups.length > 1 ? setElementToMoveSection : undefined;

        if (isTask) {
            const task = item as TaskType;
            if (task.edit) {
                return <EditTask saveTodo={saveTodo} task={task} />;
            }

            return (
                <Task
                    todo={task}
                    toggleComplete={toggleComplete}
                    deleteTodo={deleteTodo}
                    editTodo={editTodo}
                    updateTodo={updateTodo}
                    subtasks={task.subtasks || item.children as TaskType[] || []}
                    lists={lists}
                    selectedListId={selectedListId}
                    moveTaskToList={moveTaskToList}
                    moveSubtaskToList={moveSubtaskToList}
                    level={depth}
                    onMoveToSection={onMoveToSection}
                    subtaskInputPortalId={getSubtaskEditorPortalId(selectedListId, task.id)}
                    onExpandSubtasks={(task.subtasks?.length || item.children?.length) ? () => {
                        setTodos(updateNodeById(todos, task.id, (node) =>
                            (node as TaskType).collapsed === false
                                ? node
                                : { ...node, collapsed: false },
                        ));
                    } : undefined}
                    onSkipToLatestOverdue={skipToLatestOverdue}
                />
            );
        }

        switch (item.type) {
            case 'note':
                return (
                    <NoteElement
                        element={item as Note}
                        deleteElement={deleteTodo}
                        lists={lists}
                        selectedListId={selectedListId}
                        moveElementToList={moveElementToList}
                        onEdit={setEditingNote}
                        onMoveToSection={onMoveToSection}
                        showPreview
                    />
                );
            case 'habit':
                return (
                    <HabitElement
                        element={item as Habit}
                        deleteElement={deleteTodo}
                        updateElement={updateTodo}
                        lists={lists}
                        selectedListId={selectedListId}
                        moveElementToList={moveElementToList}
                        isHistoryOpen={activeHabitHistoryId === item.id}
                        onOpenHistory={openHabitHistory}
                        onCloseHistory={closeHabitHistory}
                        historyMonth={activeHabitHistoryMonth}
                        onHistoryMonthChange={setActiveHabitHistoryMonth}
                        onMoveToSection={onMoveToSection}
                    />
                );
            case 'timer':
                return (
                    <TimerElement
                        element={item as Timer}
                        deleteElement={deleteTodo}
                        updateElement={updateTodo}
                        lists={lists}
                        selectedListId={selectedListId}
                        moveElementToList={moveElementToList}
                        onMoveToSection={onMoveToSection}
                    />
                );
            case 'stopwatch':
                return (
                    <StopwatchElement
                        element={item as Stopwatch}
                        deleteElement={deleteTodo}
                        updateElement={updateTodo}
                        lists={lists}
                        selectedListId={selectedListId}
                        moveElementToList={moveElementToList}
                        onMoveToSection={onMoveToSection}
                    />
                );
            case 'timeTracker':
                return (
                    <TimeTrackerElement
                        element={item as TimeTracker}
                        deleteElement={deleteTodo}
                        updateElement={updateTodo as (element: TimeTracker) => void}
                        lists={lists}
                        selectedListId={selectedListId}
                        moveElementToList={moveElementToList as (element: TimeTracker, listId: string) => void}
                        onMoveToSection={onMoveToSection}
                    />
                );
            default:
                return null;
        }
    };

    const addOptions: Array<{ type: AddOptionType; icon: typeof faCheck; className: string }> = [
        { type: 'task', icon: faCheck, className: 'split-add-icon-task' },
        { type: 'note', icon: faNoteSticky, className: 'split-add-icon-note' },
        { type: 'habit', icon: faRepeat, className: 'split-add-icon-habit' },
        { type: 'timer', icon: faClock, className: 'split-add-icon-timer' },
        { type: 'stopwatch', icon: faStopwatch, className: 'split-add-icon-stopwatch' },
        { type: 'timeTracker', icon: faRoute, className: 'split-add-icon-tracker' },
    ];
    const openAddFlow = (groupId: string, type = addType) => {
        setActiveAddGroupId(groupId);
        closeAddMenu();
        if (type === 'task') setExpandedTaskGroupId(groupId);
        if (type === 'timer') setShowAddTimerModal(true);
        if (type === 'stopwatch') setShowAddStopwatchModal(true);
        if (type === 'timeTracker') setShowAddTimeTrackerModal(true);
        if (type === 'note') setShowNoteModal(true);
        if (type === 'habit') setShowAddHabitModal(true);
    };

    const selectAddType = (groupId: string, type: AddOptionType) => {
        setAddType(type);
        window.localStorage.setItem('minderBox:lastAddType', type);
        openAddFlow(groupId, type);
    };

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const toCollectionTreeItems = (elements: Element[], inheritedGroupId?: string): any[] => elements.map((element) => {
        const groupId = element.groupId || inheritedGroupId || 'general';
        const isTask = element.type === 'task' || !element.type;
        const task = element as TaskType;
        const children = isTask ? toCollectionTreeItems((task.subtasks || []) as Element[], groupId) : [];
        return {
            ...element,
            groupId,
            collapsed: isTask ? (task.collapsed ?? children.length > 0) : false,
            canHaveChildren: isTask
                ? (draggedItem: any) => draggedItem.type === 'task' || !draggedItem.type
                : false,
            children,
        };
    });

    const replaceGroupTree = (groupId: string, updatedItems: Element[]) => {
        const result: Element[] = [];
        let inserted = false;
        todos.forEach((item) => {
            if ((item.groupId || 'general') === groupId) {
                if (!inserted) result.push(...updatedItems);
                inserted = true;
            } else {
                result.push(item);
            }
        });
        if (!inserted) result.push(...updatedItems);
        return result;
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */

    return (
        <div className={`steps-container ${showReorderHandles ? 'reorder-enabled' : ''}`} ref={scrollRef}>
            {groups.map((group) => {
                const items = todos.filter((item) => (item.groupId || 'general') === group.id);
                const sectionHasCompletedTasks = hasCompletedTasks(items);
                const treeItems = toCollectionTreeItems(items, group.id) as TreeElement[];
                const treeRenderContext = {
                    renderElement,
                    subtaskEditorSlotsAfterItem: getSubtaskEditorSlotsAfterItem(treeItems, selectedListId),
                };
                return (
                    <section key={group.id} className={`element-section ${group.collapsed ? 'element-section-collapsed' : ''}`}>
                        <div className="element-section-header" onClick={() => toggleGroup?.(group.id)}>
                            <FontAwesomeIcon icon={group.collapsed ? faChevronRight : faChevronDown} className="section-collapse-icon" />
                            {group.name}<span className="section-count">{items.length}</span>
                            <div className="section-actions" onClick={(event) => event.stopPropagation()}>
                                <ActionMenu icon={undefined} buttonStyle={undefined} actions={[
                                    {
                                        label: t('sections.renameSection'),
                                        icon: faPencil,
                                        callback: () => setGroupToRename(group),
                                    },
                                    ...(sectionHasCompletedTasks ? [{
                                        label: t('sections.deleteCompletedTasks'),
                                        icon: faCheckDouble,
                                        callback: () => setGroupToClearCompleted(group),
                                    }] : []),
                                    ...(groups.length > 1 ? [{
                                        label: t('sections.deleteSection'),
                                        icon: faTrash,
                                        callback: () => setGroupToDelete(group),
                                    }] : []),
                                ]} />
                            </div>
                        </div>
                        {!group.collapsed && (
                            <>
                                <div className="section-elements-list collection-tree">
                                    {/* eslint-disable @typescript-eslint/no-explicit-any */}
                                    <TreeRenderContext.Provider value={treeRenderContext}>
                                        <SortableTree
                                            key={`${group.id}-${showReorderHandles ? 'sorting-enabled' : 'sorting-disabled'}`}
                                            items={treeItems}
                                            onItemsChanged={(newItems: any) => {
                                                setTodos(replaceGroupTree(group.id, fromTreeItems(newItems as any)));
                                            }}
                                            TreeItemComponent={TreeItemComponent as any}
                                            indentationWidth={TREE_INDENTATION_WIDTH}
                                            disableSorting={!showReorderHandles}
                                            canRootHaveChildren
                                        />
                                    </TreeRenderContext.Provider>
                                    {/* eslint-enable @typescript-eslint/no-explicit-any */}
                                </div>
                                {expandedTaskGroupId === group.id && (
                                    <AddElement
                                        addElement={(element) => addElement(element, group.id)}
                                        visibleTypes={['task']}
                                        initiallyExpanded
                                        autoExpandKey={`${selectedListId}:${group.id}`}
                                        draftKey={`${selectedListId}:${group.id}`}
                                        hideCollapsedButton
                                        onCollapse={() => setExpandedTaskGroupId(null)}
                                    />
                                )}
                                <div className="split-add-container">
                                    <button type="button" className="split-add-main" onClick={() => openAddFlow(group.id)}>
                                        <FontAwesomeIcon icon={faPlus} /> {t(`addTask.add${addType.charAt(0).toUpperCase() + addType.slice(1)}Label`)}
                                    </button>
                                    <button type="button" className="split-add-toggle" aria-label={t('sections.chooseElementType')} onClick={(event) => {
                                        if (addMenuGroupId === group.id) {
                                            closeAddMenu();
                                        } else {
                                            setAddMenuGroupId(group.id);
                                            setAddMenuAnchor(event.currentTarget);
                                        }
                                    }}>
                                        <FontAwesomeIcon icon={faCaretDown} />
                                    </button>
                                </div>
                            </>
                        )}
                    </section>
                );
            })}
            {addMenuGroupId && createPortal(
                <>
                    <div className="split-add-overlay" onClick={closeAddMenu} />
                    <div
                        className="split-add-menu"
                        style={{ top: addMenuPosition.top, left: addMenuPosition.left }}
                        role="menu"
                    >
                        {addOptions.map(({ type, icon, className }) => (
                            <button key={type} type="button" role="menuitem" onClick={() => selectAddType(addMenuGroupId, type)}>
                                <span className={`split-add-menu-icon ${className}`}>
                                    <FontAwesomeIcon icon={icon} />
                                </span>
                                <span>{t(`addTask.add${type.charAt(0).toUpperCase() + type.slice(1)}Label`)}</span>
                            </button>
                        ))}
                    </div>
                </>,
                document.body,
            )}
            {elementToMoveSection && createPortal(
                <div className="section-move-overlay" onClick={() => setElementToMoveSection(null)}>
                    <div className="section-move-dialog" onClick={(event) => event.stopPropagation()}>
                        <h3>{t('sections.moveToSectionTitle')}</h3>
                        <div className="section-move-list">
                            {groups
                                .filter((group) => group.id !== (elementToMoveSection.groupId || 'general'))
                                .map((group) => (
                                    <button
                                        key={group.id}
                                        type="button"
                                        className="section-move-option"
                                        onClick={() => moveElementToSection(elementToMoveSection, group.id)}
                                    >
                                        <FontAwesomeIcon icon={faLayerGroup} />
                                        <span>{group.name}</span>
                                    </button>
                                ))}
                        </div>
                        <button
                            type="button"
                            className="section-move-cancel"
                            onClick={() => setElementToMoveSection(null)}
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>,
                document.body,
            )}
            <SectionModal
                isOpen={!!groupToRename}
                initialName={groupToRename?.name || ''}
                onClose={() => setGroupToRename(null)}
                onSave={(name) => {
                    if (groupToRename) {
                        renameGroup?.(groupToRename.id, name);
                    }
                    setGroupToRename(null);
                }}
            />
            {groupToDelete && (
                <ConfirmationDialog
                    title={t('sections.deleteSectionTitle')}
                    message={t('sections.deleteSectionConfirm', { name: groupToDelete.name })}
                    onCancel={() => setGroupToDelete(null)}
                    onConfirm={() => {
                        deleteGroup?.(groupToDelete.id);
                        setGroupToDelete(null);
                    }}
                    isDanger
                />
            )}
            {groupToClearCompleted && (
                <ConfirmationDialog
                    title={t('sections.deleteCompletedTasksTitle')}
                    message={t('sections.deleteCompletedTasksConfirm', { name: groupToClearCompleted.name })}
                    onCancel={() => setGroupToClearCompleted(null)}
                    onConfirm={() => {
                        clearCompletedTasksInGroup(groupToClearCompleted.id);
                        setGroupToClearCompleted(null);
                    }}
                    isDanger
                />
            )}
            <AddTimerModal
                isOpen={showAddTimerModal}
                onClose={() => setShowAddTimerModal(false)}
                onAdd={(element) => addElement(element, activeAddGroupId)}
            />
            <AddStopwatchModal
                isOpen={showAddStopwatchModal}
                onClose={() => setShowAddStopwatchModal(false)}
                onAdd={(element) => addElement(element, activeAddGroupId)}
            />
            <AddTimeTrackerModal
                isOpen={showAddTimeTrackerModal}
                onClose={() => setShowAddTimeTrackerModal(false)}
                onAdd={(element) => addElement(element, activeAddGroupId)}
            />
            <NoteModal
                isOpen={showNoteModal || !!editingNote}
                onClose={() => {
                    setShowNoteModal(false);
                    setEditingNote(undefined);
                }}
                onAdd={(element) => addElement(element, activeAddGroupId)}
                editElement={editingNote}
                onUpdate={updateTodo}
            />
            <AddHabitModal
                isOpen={showAddHabitModal}
                onClose={() => setShowAddHabitModal(false)}
                onAdd={(element) => addElement(element, activeAddGroupId)}
            />
        </div>
    );
};
